import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GeneralService } from './general.service';
import { catchError, Observable, of, take } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AnalisisResponse, CotizacionPayload } from '../models/static-model/analisis.model';
import { BureauResponse, ClienteSTP, newClienteSTP } from '../models/static-model/bureau.model';
import { AdminAriza, ArizaData } from '../models/static-model/admins.model';
import { Deadicionales } from '../models/static-model/suppliers.model';
import { mifielStartData } from '../models/static-model/mifiel.model';

@Injectable({
  providedIn: 'root',
})
export class StaticPageService {
  public URL_API = environment.apiURLNew;

  constructor(
    private http: HttpClient,
    private generalService: GeneralService,
  ) {}

  ////////// Analisis //////////
  public getAnalisis(quotationId: number | string): Observable<AnalisisResponse | null> {
    return this.http.get<AnalisisResponse>(this.URL_API + '/getAnalisis/' + quotationId, {}).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  ////////// Resumen //////////
  public getResumenArrendamiento(
    quotationId: number | string,
  ): Observable<CotizacionPayload | null> {
    return this.http
      .get<CotizacionPayload>(this.URL_API + '/getResumenArrendamiento/' + quotationId, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  ////////// BURÓ DE CRÉDITO //////////
  public getCreditBureauUserInfo(quotationId: number | string): Observable<{
    iD_COTIZACION: number;
    iD_CARATULA: number;
    iD_PERSONA: number;
  } | null> {
    return this.http
      .get<{
        iD_COTIZACION: number;
        iD_CARATULA: number;
        iD_PERSONA: number;
      }>(this.URL_API + '/getCreditBureauUserInfo/' + quotationId, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getBureauInitialData(quotationId: number | string): Observable<{
    iD_CARATULA: number;
    tipo: string;
    persona: string;
  } | null> {
    return this.http
      .get<{
        iD_CARATULA: number;
        tipo: string;
        persona: string;
      }>(this.URL_API + '/getBureauInitialData/' + quotationId, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getBureauData(body: {
    quotationId: number | string;
    idCover: number | string;
    idUsuario: number | string;
    persona: number | string;
  }): Observable<BureauResponse | null> {
    return this.http
      .get<BureauResponse>(
        this.URL_API +
          `/getBureauData/${body.quotationId}/${body.idCover}/${body.idUsuario}/${body.persona}/`,
        {},
      )
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public newBureauData(body: {
    quotationId: number | string;
    coverId: number | string;
    userId: number | string;
    personType: number;
  }): Observable<BureauResponse | null> {
    const params = this.generalService.buildParams(body);
    return this.http
      .post<BureauResponse>(this.URL_API + `/api/CreditBureau/getBureauData`, {}, { params })
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  ////////// Confirmar Cliente //////////

  public getSTPInitialData(quotationId: number | string): Observable<ClienteSTP | null> {
    return this.http.get<ClienteSTP>(this.URL_API + `/getSTPInitialData/${quotationId}`, {}).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public confirmClient(body: {
    clabeSTP: string;
    idCliente: number;
    idCotizacion: number;
    idUsuario: number;
    materno: string;
    mode: number;
    nombre: string;
    paterno: string;
  }): Observable<ClienteSTP | null> {
    return this.http.post<ClienteSTP>(this.URL_API + `/confirmClient`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public upsertClient(body: {
    hcv: string;
    idCotizacion: number;
    idUsuario: number;
    materno: string;
    nombre: string;
    paterno: string;
    rfc: string;
  }): Observable<ClienteSTP | null> {
    return this.http.post<ClienteSTP>(this.URL_API + `/upsertClient`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  // Envio carta bienvenida por mail
  public approveQuotationMail(idCotizacion: number | string): Observable<{
    data: string;
    errors: string[];
    hasErrors: true;
  } | null> {
    return this.http
      .post<{
        data: string;
        errors: string[];
        hasErrors: true;
      }>(this.URL_API + `/approveQuotationMail/${idCotizacion}`, {})
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  ////////// Carta Promesa //////////

  public getBallonInfo(quotationId: string | number): Observable<{ n_BALLON: number }> {
    return this.http.get<{ n_BALLON: number }>(
      `${environment.apiURLNew}/getBallonInfo/${quotationId}`,
    );
  }

  ////////// Administrativos //////////

  public getAdminInitData(quotationId: string | number): Observable<ArizaData> {
    return this.http.get<ArizaData>(`${environment.apiURLNew}/getAdminInitData/${quotationId}`);
  }

  public updateAdminData(body: {
    campo: string;
    idCotizacion: number;
    idUsuario: number;
    valor: string;
  }): Observable<ArizaData> {
    return this.http.post<ArizaData>(`${environment.apiURLNew}/updateAdminData`, body);
  }

  public updateAdminClientData(body: {
    fieldName: string;
    idCliente: number;
    idCotizacion: number;
    value: string;
  }): Observable<ArizaData> {
    return this.http.post<ArizaData>(`${environment.apiURLNew}/updateAdminClientData`, body);
  }

  public upsertAdminAriza(ArizaData: AdminAriza): Observable<any> {
    return this.http.post<any>(`${environment.apiURLNew}/upsertAdminAriza`, ArizaData);
  }

  ////////// CAPTURAR DATOS PROVEEDORES //////////

  public getDeadicionales(quotationId: string | number): Observable<Deadicionales[] | null> {
    return this.http
      .get<Deadicionales[]>(`${environment.apiURLNew}/getDeadicionales/${quotationId}`)
      .pipe(
        take(1),
        catchError(() => {
          return of(null);
        }),
      );
  }

  public getProveedores(): Observable<any[] | null> {
    return this.http.get<any[]>(`${environment.apiURLNew}/getProveedores`).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  // MiFiel: (CONTRATOS) //

  public getMiFielInitialData(body: {
    CaratulaId?: number;
    MifielIds: number;
    QuotationId: number;
  }): Observable<mifielStartData | null> {
    const params = this.generalService.buildParams(body);
    return this.http.get<mifielStartData>(this.URL_API + `/GetMiFielInitialData`, { params }).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public checkStatusMifiel(body: { idContract: number | string }): Observable<any | null> {
    const params = this.generalService.buildParams(body);
    return this.http.get<any>(this.URL_API + `/api/Document/CheckStatusMifiel`, { params }).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public signContract(body: {
    QuotationId: number | string;
    DocumentName: number | string;
    IdSigners: number | string;
    IdRepresentante: number | string;
    MifielId: number | string;
  }): Observable<any | null> {
    return this.http.post<any>(this.URL_API + `/SignContractV2`, body).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }

  public cancelContract(body: { mifielQuotationId: number }): Observable<any | null> {
    const params = this.generalService.buildParams(body);
    return this.http.post<any>(this.URL_API + `/CancelMifielContract`, params).pipe(
      take(1),
      catchError(() => {
        return of(null);
      }),
    );
  }
}
